import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { BotRule } from "@/lib/bot-funnel";
import type { UrlActiveFilters } from "@/lib/url-active-filters";
import { BotDetectionService } from "@/services/BotDetectionService";

export const useBotRules = () =>
  useQuery({ queryKey: ["bot-rules"], queryFn: BotDetectionService.getRules });

/**
 * The funnel rows for a period and the Clicks filters. The bot filter is left
 * out of the key (the server ignores it), so applying a preset never refetches.
 */
export const useBotFunnel = (from: string | null, to: string, filters: UrlActiveFilters = {}, enabled = true) => {
  const rest = { ...filters };
  delete rest.is_own_bot;
  return useQuery({
    queryKey: ["bot-funnel", from, to, rest],
    queryFn: () => BotDetectionService.getFunnel(from, to, rest),
    placeholderData: keepPreviousData,
    enabled,
    // Shown in the window / tab, not as the whole page's error screen.
    throwOnError: false,
  });
};

export const useSaveBotRules = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (rules: Pick<BotRule, "key" | "enabled" | "patterns">[]) => {
      for (const r of rules) await BotDetectionService.saveRule(r.key, { enabled: r.enabled, patterns: r.patterns });
    },
    onSuccess: (_, rules) => {
      // The saved rules at once, so the funnel does not flick back to the old
      // ones while they reload; then every Bots number and the hide-bots filter.
      queryClient.setQueryData<BotRule[]>(["bot-rules"], (old) =>
        old?.map((r) => {
          const s = rules.find((x) => x.key === r.key);
          return s ? { ...r, enabled: s.enabled, patterns: s.patterns } : r;
        }),
      );
      return queryClient.invalidateQueries();
    },
  });
};
